import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { parseErrorDetail } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';

export const METADATA_COUNTRIES = {
  Global: [], NA: ['United States', 'Canada', 'Mexico'],
  EMEA: ['United Kingdom', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'Sweden', 'Poland', 'Ireland', 'Belgium'],
  APAC: ['India', 'Australia', 'Singapore', 'Japan', 'Philippines', 'Malaysia', 'China', 'South Korea', 'Hong Kong', 'New Zealand'],
  LATAM: ['Brazil', 'Argentina', 'Colombia', 'Chile', 'Peru', 'Costa Rica', 'Panama'],
};
const TEXT_FIELDS = { title: 'Title', short_summary: 'Short Description', long_summary: 'Long Description', tags: 'Tags' };
const SUGGESTED_FIELDS = { title: 'Title', short_summary: 'Short Description', long_summary: 'Long Description', topic: 'Topic', sub_topic: 'Sub-topic', region: 'Region', country: 'Country' };

export function metadataError(error) {
  return error?.isNetworkError
    ? 'Eva could not be reached. Your edits are still here. Please try again.'
    : parseErrorDetail(error).message;
}

function stringList(value) {
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { value = value.split(',').map(v => v.trim()).filter(Boolean); }
  }
  return Array.isArray(value) ? value.filter(v => typeof v === 'string') : [];
}
export function metadataForm(data) {
  return { title: data.title || '', short_summary: data.short_summary || '', long_summary: data.long_summary || '',
    topic: data.topic || '', sub_topic: data.sub_topic || '', region: data.region || '', country: data.country || '',
    tags: stringList(data.tags).join(', '), group_visibility: stringList(data.group_visibility).length ? stringList(data.group_visibility) : ['all'] };
}
function values(form) {
  return { ...form, title: form.title?.trim(), tags: [...new Set((form.tags || '').split(',').map(v => v.trim()).filter(Boolean))],
    group_visibility: [...form.group_visibility].sort() };
}
export function changedMetadata(form, original, isAdmin) {
  const next = values(form), before = values(original);
  return Object.fromEntries(Object.entries(next).filter(([key, value]) =>
    (isAdmin || key !== 'group_visibility') && JSON.stringify(value) !== JSON.stringify(before[key])));
}

export function useMetadataOptions(isAdmin) {
  const [topics, setTopics] = useState([]), [groups, setGroups] = useState([]);
  const [error, setError] = useState(''), [loading, setLoading] = useState(true), [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    Promise.all([api.get('/topics/flat'), isAdmin ? api.get('/groups') : Promise.resolve({ data: [] })])
      .then(([t, g]) => { if (active) {
        setTopics((Array.isArray(t.data) ? t.data : []).filter(v => v.status !== 'deprecated'));
        setGroups((Array.isArray(g.data) ? g.data : (g.data?.groups || [])).filter(group =>
          group.id !== 'all' && group.slug !== 'all-employees' && group.name?.trim().toLowerCase() !== 'all employees'));
      } })
      .catch(() => { if (active) setError('Topics and groups could not be loaded. Your edits are still here.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAdmin, version]);
  return { topics, groups, error, loading, retry: () => setVersion(v => v + 1) };
}

export function MetadataSelect({ label, value, onChange, options, disabled, emptyLabel = 'None', id }) {
  const selected = options.some(o => o.value === value);
  return <div className="min-w-0 space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Select value={value || '__empty'} onValueChange={v => onChange(v === '__empty' ? '' : v)} disabled={disabled}>
      <SelectTrigger id={id} className="h-11 w-full min-w-0"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="__empty">{emptyLabel}</SelectItem>
        {value && !selected && <SelectItem value={value}>{value} (current)</SelectItem>}
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>;
}

export function MetadataFields({ form, setForm, topics, groups, isAdmin, disabled, bulk = false, sections = [] }) {
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const subTopics = topics.find(t => t.name === form.topic)?.sub_topics?.filter(s => s.status !== 'deprecated') || [];
  const options = list => list.map(value => ({ value }));
  return <div className="space-y-4">
    {!bulk && Object.entries(TEXT_FIELDS).map(([key, label]) => <div key={key} className="space-y-1.5">
      <Label htmlFor={`metadata-${key}`}>{label}</Label>
      {key.endsWith('summary') ? <Textarea id={`metadata-${key}`} rows={key === 'long_summary' ? 5 : 3} disabled={disabled}
        value={form[key]} onChange={e => set(key, e.target.value)} className="min-h-[88px]" /> :
        <Input id={`metadata-${key}`} disabled={disabled} value={form[key]} onChange={e => set(key, e.target.value)}
          maxLength={key === 'title' ? 300 : undefined} className="h-11" />}
      {key === 'tags' && <p className="text-xs text-muted-foreground">Separate tags with commas.</p>}
    </div>)}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {(!bulk || sections.includes('topic')) && <>
      <MetadataSelect id="metadata-topic" label="Topic" value={form.topic} disabled={disabled}
        options={options(topics.map(t => t.name))} emptyLabel="Uncategorized"
        onChange={topic => setForm(prev => ({ ...prev, topic, sub_topic: '' }))} />
      <MetadataSelect id="metadata-sub-topic" label="Sub-topic" value={form.sub_topic} disabled={disabled || !form.topic}
        options={options(subTopics.map(t => t.name))} onChange={v => set('sub_topic', v)} />
      </>}
      {(!bulk || sections.includes('region')) && <>
      <MetadataSelect id="metadata-region" label="Region" value={form.region} disabled={disabled}
        options={options(Object.keys(METADATA_COUNTRIES))}
        onChange={region => setForm(prev => ({ ...prev, region, country: '' }))} />
      <MetadataSelect id="metadata-country" label="Country" value={form.country} disabled={disabled || !(METADATA_COUNTRIES[form.region]?.length)}
        options={options(METADATA_COUNTRIES[form.region] || [])} onChange={v => set('country', v)} />
      </>}
    </div>
    {isAdmin && <fieldset className="space-y-2 rounded-lg border p-3" disabled={disabled}>
      <legend className="px-1 text-sm font-medium">Group Visibility</legend>
      <p className="text-xs text-muted-foreground">Controls which employees can use this document.</p>
      {[{ id: 'all', name: 'All Employees' }, ...groups].map(group => <label key={group.id} className="flex min-h-11 items-center gap-3 text-sm">
        <Checkbox checked={form.group_visibility.includes(group.id)} disabled={disabled} onCheckedChange={checked => {
          const next = group.id === 'all' ? ['all'] : checked ? [...form.group_visibility.filter(v => v !== 'all'), group.id] : form.group_visibility.filter(v => v !== group.id);
          set('group_visibility', next.length ? next : ['all']);
        }} />{group.name}
      </label>)}
      {form.group_visibility.filter(id => id !== 'all' && !groups.some(g => g.id === id)).map(id =>
        <p key={id} className="break-all text-xs text-muted-foreground">Existing group: {id}</p>)}
    </fieldset>}
  </div>;
}

// A suggestion is a proposal. Keep it separate from the user's working copy
// until they explicitly apply selected fields, and never propose access grants.
export function metadataSuggestions(payload, topics, form) {
  const raw = payload?.suggestions;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Eva returned no usable suggestions. Your edits are unchanged.');
  const proposals = Object.fromEntries(Object.entries(SUGGESTED_FIELDS).filter(([key]) => typeof raw[key] === 'string' && raw[key].trim()).map(([key]) => [key, raw[key].trim()]));
  const notices = [];
  const topic = topics.find(t => t.name === proposals.topic);
  if (proposals.topic && (!topic || raw.topic_warning)) { delete proposals.topic; delete proposals.sub_topic; notices.push('The suggested topic is not available in this workspace.'); }
  const parent = topic || topics.find(t => t.name === form.topic);
  if (proposals.sub_topic && (raw.sub_topic_warning || !parent?.sub_topics?.some(s => s.name === proposals.sub_topic && s.status !== 'deprecated'))) {
    delete proposals.sub_topic; notices.push('The suggested sub-topic is not available for this topic.');
  }
  if (proposals.region && !Object.hasOwn(METADATA_COUNTRIES, proposals.region)) { delete proposals.region; delete proposals.country; }
  if (proposals.country && !(METADATA_COUNTRIES[proposals.region || form.region] || []).includes(proposals.country)) delete proposals.country;
  if (!Object.keys(proposals).length) throw new Error('Eva returned no usable suggestions. Your edits are unchanged.');
  if (payload.metadata_source === 'truncated') notices.push(`Suggestions use the first ${payload.max_pdf_pages || 100} pages. Review them against the full document.`);
  if (payload.metadata_source === 'text') notices.push('Suggestions use the extracted text.');
  return { proposals, notices, selected: Object.keys(proposals) };
}

const DocumentMetadataEditor = forwardRef(function DocumentMetadataEditor({ data, isAdmin, onSaved, onCancel, suggestOnOpen = false }, ref) {
  const [form, setForm] = useState(() => metadataForm(data));
  const original = useRef(metadataForm(data));
  const revision = useRef(data.updated_at ?? null);
  const [error, setError] = useState(''), [busy, setBusy] = useState(''), [suggestions, setSuggestions] = useState(null);
  const options = useMetadataOptions(isAdmin);
  const [ConfirmEl, confirm] = useConfirm();
  const mounted = useRef(true), pending = useRef(false), startedSuggestion = useRef(false);
  const changes = changedMetadata(form, original.current, isAdmin);
  const dirty = Object.keys(changes).length > 0;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const mayDiscard = async () => {
    if (pending.current) return false;
    return !dirty || await confirm({ title: 'Discard unsaved changes?', description: 'Your metadata edits have not been saved.', confirmLabel: 'Discard Changes', variant: 'warning' });
  };
  useImperativeHandle(ref, () => ({ mayDiscard }));
  const suggest = async () => {
    if (pending.current || options.loading || options.error) return;
    pending.current = true; setBusy('suggest'); setError(''); setSuggestions(null);
    try {
      const response = await api.post(`/kb/documents/${data.id}/suggest-metadata`);
      if (mounted.current) setSuggestions(metadataSuggestions(response.data, options.topics, form));
    } catch (err) { if (mounted.current) setError(metadataError(err)); }
    finally { pending.current = false; if (mounted.current) setBusy(''); }
  };
  useEffect(() => {
    if (suggestOnOpen && !options.loading && !options.error && !startedSuggestion.current) {
      startedSuggestion.current = true; suggest();
    }
  });
  const applySuggestions = () => {
    const chosen = Object.fromEntries(suggestions.selected.map(key => [key, suggestions.proposals[key]]));
    setForm(prev => {
      const next = { ...prev, ...chosen };
      if (chosen.topic && chosen.topic !== prev.topic && !chosen.sub_topic) next.sub_topic = '';
      if (next.sub_topic && !options.topics.find(t => t.name === next.topic)?.sub_topics?.some(s => s.name === next.sub_topic && s.status !== 'deprecated')) next.sub_topic = '';
      if (chosen.region && chosen.region !== prev.region && !chosen.country) next.country = '';
      if (chosen.country && !(METADATA_COUNTRIES[next.region] || []).includes(chosen.country)) next.country = '';
      return next;
    });
    setSuggestions(null);
  };
  const save = async () => {
    if (pending.current || !dirty) return;
    if (!form.title.trim()) { setError('Enter a document title.'); document.getElementById('metadata-title')?.focus(); return; }
    pending.current = true; setBusy('save'); setError('');
    try {
      const response = await api.put(`/kb/documents/${data.id}/metadata`, { ...changes, expected_updated_at: revision.current });
      if (mounted.current) onSaved({ ...changes, ...response?.data?.metadata, updated_at: response?.data?.updated_at || data.updated_at });
    } catch (err) { if (mounted.current) setError(metadataError(err)); }
    finally { pending.current = false; if (mounted.current) setBusy(''); }
  };
  return <>{ConfirmEl}<div className="flex min-h-0 flex-1 flex-col">
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-3">
      <p className="text-sm text-muted-foreground">{['published', 'live'].includes(data.status) ? 'Updating metadata keeps the document published. ' : ''}Only admins can change group visibility.</p>
      {options.error && <div role="alert" className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive-text">{options.error}<Button variant="outline" onClick={options.retry} className="mt-2 block">Retry options</Button></div>}
      <Button variant="outline" onClick={suggest} disabled={!!busy || options.loading || !!options.error} className="min-h-11">
        {busy === 'suggest' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Suggest metadata
      </Button>
      {suggestions && <section aria-label="Review Eva suggestions" className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
        <h3 className="font-semibold">Review Eva suggestions</h3>
        <p className="text-sm text-muted-foreground">Choose what to use. Nothing is saved yet.</p>
        {suggestions.notices.map(note => <p key={note} className="text-sm">{note}</p>)}
        {Object.entries(suggestions.proposals).map(([key, value]) => <label key={key} className="flex gap-3 rounded-md border bg-background p-3 text-sm">
          <Checkbox className="mt-1 shrink-0" checked={suggestions.selected.includes(key)} onCheckedChange={checked => setSuggestions(prev => ({ ...prev, selected: checked ? [...prev.selected, key] : prev.selected.filter(k => k !== key) }))} />
          <span className="min-w-0"><span className="block font-medium">{SUGGESTED_FIELDS[key]}</span><span className="block max-h-40 overflow-auto whitespace-pre-wrap break-words">{value}</span></span>
        </label>)}
        <div className="flex flex-wrap gap-2"><Button className="min-h-11" onClick={applySuggestions} disabled={!suggestions.selected.length}>Use selected suggestions</Button><Button className="min-h-11" variant="outline" onClick={() => setSuggestions(null)}>Dismiss suggestions</Button></div>
      </section>}
      <MetadataFields form={form} setForm={setForm} topics={options.topics} groups={options.groups} isAdmin={isAdmin} disabled={!!busy || options.loading} />
    </div>
    <div className="space-y-3 border-t pt-3">
      {error && <p role="alert" className="text-sm text-destructive-text">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2"><Button className="min-h-11" variant="outline" disabled={!!busy} onClick={async () => { if (await mayDiscard()) onCancel(); }}>Cancel</Button>
        <Button disabled={!!busy || !dirty} onClick={save} data-testid="save-metadata-btn" className={`min-h-11 ${dirty ? 'ring-2 ring-primary/30' : ''}`}>{busy === 'save' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Changes</Button></div>
    </div>
  </div></>;
});
export default DocumentMetadataEditor;
